import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Save, Loader2, RefreshCw, Building2, Mail, Phone, MapPin, Globe, Facebook, Instagram, Twitter } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    business_name: '',
    business_legal_name: '',
    tagline: '',
    email: '',
    legal_email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: ''
    },
    social_links: {
      facebook: '',
      instagram: '',
      twitter: ''
    },
    currency: 'USD',
    currency_symbol: '$'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings/`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/site-settings/`, settings);
      toast({
        title: 'Settings saved!',
        description: 'Your site settings have been updated successfully.'
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset to default settings?')) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/site-settings/reset`);
      setSettings(response.data);
      toast({
        title: 'Settings reset',
        description: 'Settings have been restored to defaults.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reset settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateAddress = (field, value) => {
    setSettings(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  const updateSocial = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: value }
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-charcoal font-heading">Site Settings</h1>
          <Button 
            variant="outline" 
            onClick={handleReset}
            className="text-text-muted hover:text-text-heading"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Business Information */}
          <div className="bg-white rounded-xl p-8 border border-soft-sand shadow-soft">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="h-6 w-6 text-accent" />
              <h2 className="text-2xl font-bold text-charcoal font-heading">Business Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="font-body font-semibold">Business Name</Label>
                <Input
                  value={settings.business_name}
                  onChange={(e) => setSettings({...settings, business_name: e.target.value})}
                  className="mt-2 font-body"
                  placeholder="BOXVERRA"
                />
                <p className="text-sm text-text-muted mt-1">Displayed in header, footer, etc.</p>
              </div>
              <div>
                <Label className="font-body font-semibold">Legal Name</Label>
                <Input
                  value={settings.business_legal_name}
                  onChange={(e) => setSettings({...settings, business_legal_name: e.target.value})}
                  className="mt-2 font-body"
                  placeholder="BOXVERRA, Inc."
                />
                <p className="text-sm text-text-muted mt-1">For legal documents & policies</p>
              </div>
              <div className="md:col-span-2">
                <Label className="font-body font-semibold">Tagline</Label>
                <Input
                  value={settings.tagline}
                  onChange={(e) => setSettings({...settings, tagline: e.target.value})}
                  className="mt-2 font-body"
                  placeholder="We create comfort in your life"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-xl p-8 border border-soft-sand shadow-soft">
            <div className="flex items-center gap-3 mb-6">
              <Mail className="h-6 w-6 text-accent" />
              <h2 className="text-2xl font-bold text-charcoal font-heading">Contact Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="font-body font-semibold">Support Email</Label>
                <Input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({...settings, email: e.target.value})}
                  className="mt-2 font-body"
                  placeholder="support@boxverra.com"
                />
                <p className="text-sm text-text-muted mt-1">Main contact email</p>
              </div>
              <div>
                <Label className="font-body font-semibold">Legal Email</Label>
                <Input
                  type="email"
                  value={settings.legal_email}
                  onChange={(e) => setSettings({...settings, legal_email: e.target.value})}
                  className="mt-2 font-body"
                  placeholder="legal@boxverra.com"
                />
                <p className="text-sm text-text-muted mt-1">For legal inquiries</p>
              </div>
              <div className="md:col-span-2">
                <Label className="font-body font-semibold">Phone Number</Label>
                <Input
                  value={settings.phone}
                  onChange={(e) => setSettings({...settings, phone: e.target.value})}
                  className="mt-2 font-body"
                  placeholder="+1 (800) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl p-8 border border-soft-sand shadow-soft">
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="h-6 w-6 text-accent" />
              <h2 className="text-2xl font-bold text-charcoal font-heading">Business Address</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label className="font-body font-semibold">Street Address</Label>
                <Input
                  value={settings.address.street}
                  onChange={(e) => updateAddress('street', e.target.value)}
                  className="mt-2 font-body"
                  placeholder="123 Serenity Lane"
                />
              </div>
              <div>
                <Label className="font-body font-semibold">City</Label>
                <Input
                  value={settings.address.city}
                  onChange={(e) => updateAddress('city', e.target.value)}
                  className="mt-2 font-body"
                  placeholder="San Francisco"
                />
              </div>
              <div>
                <Label className="font-body font-semibold">State/Province</Label>
                <Input
                  value={settings.address.state}
                  onChange={(e) => updateAddress('state', e.target.value)}
                  className="mt-2 font-body"
                  placeholder="CA"
                />
              </div>
              <div>
                <Label className="font-body font-semibold">ZIP/Postal Code</Label>
                <Input
                  value={settings.address.zip}
                  onChange={(e) => updateAddress('zip', e.target.value)}
                  className="mt-2 font-body"
                  placeholder="94102"
                />
              </div>
              <div>
                <Label className="font-body font-semibold">Country</Label>
                <Input
                  value={settings.address.country}
                  onChange={(e) => updateAddress('country', e.target.value)}
                  className="mt-2 font-body"
                  placeholder="United States"
                />
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-xl p-8 border border-soft-sand shadow-soft">
            <div className="flex items-center gap-3 mb-6">
              <Globe className="h-6 w-6 text-accent" />
              <h2 className="text-2xl font-bold text-charcoal font-heading">Social Media Links</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="font-body font-semibold flex items-center gap-2">
                  <Facebook className="h-4 w-4 text-blue-600" />
                  Facebook
                </Label>
                <Input
                  value={settings.social_links.facebook}
                  onChange={(e) => updateSocial('facebook', e.target.value)}
                  className="mt-2 font-body"
                  placeholder="https://facebook.com/..."
                />
              </div>
              <div>
                <Label className="font-body font-semibold flex items-center gap-2">
                  <Instagram className="h-4 w-4 text-pink-600" />
                  Instagram
                </Label>
                <Input
                  value={settings.social_links.instagram}
                  onChange={(e) => updateSocial('instagram', e.target.value)}
                  className="mt-2 font-body"
                  placeholder="https://instagram.com/..."
                />
              </div>
              <div>
                <Label className="font-body font-semibold flex items-center gap-2">
                  <Twitter className="h-4 w-4 text-sky-500" />
                  Twitter / X
                </Label>
                <Input
                  value={settings.social_links.twitter}
                  onChange={(e) => updateSocial('twitter', e.target.value)}
                  className="mt-2 font-body"
                  placeholder="https://x.com/..."
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex gap-4">
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-accent hover:bg-accent-hover text-white px-10 py-6 font-body text-lg"
            >
              {saving ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
